import {
  BaseEventService,
  type ServiceActivationContext,
  type LifecycleContext,
} from '@realitycollective/service-framework';
import { createLogger } from '../../util/logger';
import type { WebRTCConnectionState } from '../webrtc/IWebRTCService';
import type { IImageQualifierService } from '../qualifier/IImageQualifierService';
import {
  parseAssistRemoteConfig,
  resolveTokenEndpoint,
  resolveWearerSecret,
  normalizeConfigEndpoint,
  parseWearerTokenResponse,
  wearerTokenRequestJson,
  type AssistRemoteConfig,
} from '../../protocol/assistRemoteConfig';
import {
  parseHostStatusDocument,
  resolveHostStatusEndpoint,
} from '../../protocol/hostLinkStatus';
import {
  classifyTokenRefusal,
  describeTokenRefusal,
  type TokenRefusal,
} from '../../protocol/tokenRefusal';
import { RoomTopics } from '../../protocol/roomTopics';
import { ServerAvailabilityModel } from '../../core/serverAvailabilityModel';
import type {
  ILiveKitRoomService,
  LiveKitRoomEventMap,
  RoomParticipant,
  RoomTrackKind,
} from './ILiveKitRoomService';

const log = createLogger('Room');

/**
 * The slice of `livekit-client` this service uses, held structurally so tests
 * can substitute a fake room without the SDK. The default factory (below)
 * creates the real `Room`.
 */
export interface LocalTrackPublicationLike {
  mute(): Promise<unknown>;
  unmute(): Promise<unknown>;
}

export interface RemoteTrackPublicationLike {
  readonly kind: string;
  readonly isMuted?: boolean;
  setSubscribed(subscribed: boolean): void;
}

export interface RemoteParticipantLike {
  readonly identity: string;
  readonly name?: string;
  readonly metadata?: string;
  getTrackPublications(): readonly RemoteTrackPublicationLike[];
}

export interface RoomLike {
  readonly name?: string;
  readonly localParticipant: {
    readonly identity: string;
    publishTrack(track: MediaStreamTrack, options?: Record<string, unknown>): Promise<unknown>;
    publishData(data: Uint8Array, options?: Record<string, unknown>): Promise<void>;
  };
  readonly remoteParticipants: ReadonlyMap<string, RemoteParticipantLike>;
  connect(url: string, token: string, options?: Record<string, unknown>): Promise<void>;
  disconnect(): Promise<void>;
  on(event: string, listener: (...args: never[]) => void): unknown;
}

export interface LiveKitRoomConfig {
  /**
   * `GET /api/config` endpoint (Cloudflare Pages, KV-backed). Empty disables
   * discovery and the dev literal fallbacks below are used directly.
   */
  readonly configUrl?: string;
  /** Optional cell id appended as `?cell=` (multi-cell deployments; empty = "main"). */
  readonly cell?: string;
  /** Wearer gate secret fallback. Empty = use the secret published inside the v2 config. */
  readonly wearerToken?: string;
  /** Display name sent as the token request's `device`. */
  readonly deviceLabel?: string;
  /** Literal LiveKit URL - used when configUrl is empty or discovery fails and this is set. Development only. */
  readonly devServerUrl?: string;
  /** Literal join token - development only. */
  readonly devToken?: string;
  /** Connect as soon as the service starts (the warm-up card expects an eagerly-negotiated session). Default true. */
  readonly autoConnect?: boolean;
  /**
   * Push frames as soon as the session is up, skipping the warm-up consent.
   * OFF in production: the warm-up card's Enter is the moment camera pixels
   * may leave the device. Default false.
   */
  readonly autoStartSession?: boolean;
  /** Reconnect backoff schedule in seconds; the last entry repeats. Default [1, 2, 5, 10, 15]. */
  readonly reconnectBackoffSeconds?: readonly number[];
  /**
   * The pinned publish size (RA-C1). The detector's accuracy is a function of
   * the frame size it is fed: the server infers AS SENT, no rescale. The
   * camera track is constrained to this before publish. Default 640x480@30.
   */
  readonly streamWidth?: number;
  readonly streamHeight?: number;
  readonly targetFps?: number;
  /** Encoder max bitrate (bps) for the pinned stream. Default 1_500_000. */
  readonly maxBitrate?: number;
  /** H.264 mirrors the hardware-encoder path on Quest; false = VP8. Default true. */
  readonly useH264?: boolean;
  /** Mute the published video while the image qualifier says quality is too poor. Default false. */
  readonly gateStreamingOnQuality?: boolean;
  /** HTTP timeout for config/token/status fetches (ms). Default 6000 (mirrors Unity's 6 s). */
  readonly fetchTimeoutMs?: number;
  /** Test seam: substitute the room factory. Default builds a real livekit-client `Room`. */
  readonly roomFactory?: () => RoomLike;
  /** Test seam: substitute fetch. Default `globalThis.fetch`. */
  readonly fetchImpl?: typeof fetch;
  /** Test seam: monotonic clock in seconds. Default `performance.now()/1000`. */
  readonly nowSeconds?: () => number;
  /** Internal pump cadence (ms) - see the class doc. Default 250. */
  readonly pumpIntervalMs?: number;
}

const DEFAULT_BACKOFF: readonly number[] = [1, 2, 5, 10, 15];

interface RoomCredentials {
  url?: string;
  token?: string;
  display?: string;
  failureReason?: string;
  /** How `POST /api/token` said no, classified (Plan-06 step 5c). */
  refusal?: TokenRefusal;
}

function credentialsUsable(c: RoomCredentials): c is RoomCredentials & { url: string; token: string } {
  return !!c.url && !!c.token;
}

/**
 * The LiveKit room transport - port of the Unity `LiveKitRoomService.cs` on
 * `livekit-client`. Connects the deployment room (config -> token -> connect),
 * publishes the pinned camera track as the wearer's video (simulcast OFF,
 * degradationPreference maintain-resolution - RA-C1), publishes the
 * echo-cancelled microphone on consent (RA-C2), carries the hub data topics
 * both ways, and owns the D24 waiting window via `ServerAvailabilityModel`.
 *
 * Divergences from the Unity port, both deliberate:
 *
 * - **Self-pumping.** Unity drives the per-frame gates from `Update()`; here
 *   the service-framework bridge only ticks while an XR session is focused,
 *   and the whole warm-up flow happens on the flat page BEFORE any session
 *   exists. So the service runs its own coarse interval timer through the
 *   same `pump()` the bridge tick calls - idempotent and time-based, so
 *   double-pumping is harmless.
 * - **No publish RenderTexture.** The browser owns the capture pipeline; the
 *   resolution pin is applied as track constraints (`applyConstraints`)
 *   before publish rather than by blitting into an owned texture.
 */
export class LiveKitRoomService
  extends BaseEventService<LiveKitRoomEventMap, LiveKitRoomConfig>
  implements ILiveKitRoomService
{
  private readonly qualifier: IImageQualifierService | undefined;

  private state: WebRTCConnectionState = 'new';
  private closedByUser = false;
  private connectInFlight = false;
  private reconnectAttempt = 0;
  /** Monotonic seconds; negative = no reconnect scheduled. */
  private reconnectAtSeconds = -1;

  private room: RoomLike | undefined;
  private readonly remoteById = new Map<string, RoomParticipant>();

  private localStream: MediaStream | undefined;
  private videoPublication: LocalTrackPublicationLike | undefined;
  private videoPublishInFlight = false;
  private videoPublished = false;
  private nextVideoRetrySeconds = -1;
  private lastQualifierGateMuted = false;

  private micDesired = false;
  private micPublishInFlight = false;
  private micTrack: MediaStreamTrack | undefined;
  private audioPublication: LocalTrackPublicationLike | undefined;
  private micMuted = false;

  private readonly _availability = new ServerAvailabilityModel();
  private hostStatusEndpoint: string | null = null;
  private statusPollInFlight = false;

  private configEndpointOverride: string | null = null;
  private pumpTimer: ReturnType<typeof setInterval> | undefined;
  private unsubAvailability: (() => void) | undefined;

  private _lastDiagnostic = 'idle';
  private _lastRemoteConfig: AssistRemoteConfig | null = null;
  private _streamingRequested = false;
  private _localIdentity = '';
  private _roomName = '';
  private _activeEndpointDisplay = '';

  constructor(context: ServiceActivationContext<LiveKitRoomConfig>, qualifier?: IImageQualifierService) {
    super(context);
    this.qualifier = qualifier;
  }

  private get cfg(): LiveKitRoomConfig {
    return this.serviceConfig;
  }

  private now(): number {
    return this.cfg.nowSeconds ? this.cfg.nowSeconds() : performance.now() / 1000;
  }

  private get fetchImpl(): typeof fetch {
    return this.cfg.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  // ---------------------------------------------------------------- surface

  get connectionState(): WebRTCConnectionState {
    return this.state;
  }

  get isRoomConnected(): boolean {
    return this.state === 'connected';
  }

  get remoteParticipants(): readonly RoomParticipant[] {
    return [...this.remoteById.values()];
  }

  get localIdentity(): string {
    return this._localIdentity;
  }

  get roomName(): string {
    return this._roomName;
  }

  get activeEndpointDisplay(): string {
    return this._activeEndpointDisplay;
  }

  get lastDiagnostic(): string {
    return this._lastDiagnostic;
  }

  get streamingRequested(): boolean {
    return this._streamingRequested;
  }

  get lastRemoteConfig(): AssistRemoteConfig | null {
    return this._lastRemoteConfig;
  }

  get availability(): ServerAvailabilityModel {
    return this._availability;
  }

  get microphoneEnabled(): boolean {
    return this.micDesired && this.micTrack !== undefined && !this.micMuted;
  }

  get configEndpoint(): string {
    return this.effectiveConfigUrl;
  }

  private get effectiveConfigUrl(): string {
    return this.configEndpointOverride ?? this.cfg.configUrl ?? '';
  }

  // -------------------------------------------------------------- lifecycle

  override start(): void {
    this._streamingRequested = this.cfg.autoStartSession ?? false;
    this.unsubAvailability = this._availability.onChanged((model) =>
      this.emit('availabilityChanged', model),
    );
    // Self-pumping (see the class doc): the bridge only ticks while an XR
    // session is focused, and connect/waiting-window work must run before one
    // exists.
    this.pumpTimer = setInterval(() => this.pump(this.now()), this.cfg.pumpIntervalMs ?? 250);
    this.reportDiagnostic('idle - waiting to connect');
  }

  override update(_context: LifecycleContext): void {
    this.pump(this.now());
  }

  override destroy(): void {
    if (this.pumpTimer !== undefined) {
      clearInterval(this.pumpTimer);
      this.pumpTimer = undefined;
    }
    this.unsubAvailability?.();
    this.tearDownSession('service destroyed');
    super.destroy();
  }

  // ------------------------------------------------------------------ pump

  /** One idempotent pass over the time-based gates. Safe to call from both the interval and the bridge tick. */
  private pump(nowSeconds: number): void {
    // Scheduled reconnect (single timer - never stacked).
    if (this.reconnectAtSeconds >= 0 && nowSeconds >= this.reconnectAtSeconds) {
      this.reconnectAtSeconds = -1;
      this.beginConnect();
    }

    // The waiting window (D24): poll the READ-ONLY status route on the §9.6
    // cadence - never /api/token, which would claim the slot the instant it
    // freed.
    if (this._availability.shouldPollStatus(nowSeconds) && !this.statusPollInFlight) {
      void this.pollHostStatus();
    }

    // Eager connect: the warm-up card expects the session negotiated behind it
    // (consent gates FRAMES, not the plumbing). Suppressed while waiting even
    // once the button has enabled: the person chooses when to start (§9.6).
    if (
      (this.cfg.autoConnect ?? true) &&
      !this.closedByUser &&
      !this.connectInFlight &&
      this._availability.mayAutoConnect &&
      this.state !== 'connected' &&
      this.reconnectAtSeconds < 0
    ) {
      this.beginConnect();
    }

    // Video publish gate: room up + user consent (Enter) + camera live.
    if (
      this.isRoomConnected &&
      this._streamingRequested &&
      !this.videoPublished &&
      !this.videoPublishInFlight &&
      this.liveVideoTrack() !== undefined &&
      (this.nextVideoRetrySeconds < 0 || nowSeconds >= this.nextVideoRetrySeconds)
    ) {
      void this.publishVideo();
    }

    this.updateQualifierGate();
  }

  private liveVideoTrack(): MediaStreamTrack | undefined {
    const track = this.localStream?.getVideoTracks()[0];
    return track && track.readyState === 'live' ? track : undefined;
  }

  // ------------------------------------------------------- IWebRTC surface

  beginStreaming(): void {
    if (this._streamingRequested) return;
    this._streamingRequested = true;
    this.reportDiagnostic('frames enabled - streaming');
    this.emit('streamingBegan', undefined);
  }

  setVideoStream(stream: MediaStream): void {
    this.localStream = stream;
  }

  sendDetectionMessage(json: string): boolean {
    // The only upstream senders on this member are the training flows - on the
    // room they travel the `training` topic per the hub conventions.
    return !!json && this.publishData(RoomTopics.training, json);
  }

  async connect(): Promise<void> {
    this.closedByUser = false;

    // Someone pressed Join. That closes the waiting window and allows exactly
    // one ordinary attempt - which may lose the claim race to another waiting
    // client and land back here. That is correct: the enabled button is a
    // hint, not a reservation (§9.6).
    this._availability.onJoinRequested();

    if (!this.connectInFlight && this.state !== 'connected') {
      await this.connectFlow();
    }
  }

  close(): void {
    this.closedByUser = true;
    this.reconnectAtSeconds = -1;
    this.tearDownSession('disconnected by user');
    this.setState('closed');
  }

  // ----------------------------------------------------------- room surface

  publishData(topic: string, json: string, reliable = true): boolean {
    const room = this.room;
    if (!this.isRoomConnected || !room || !json) return false;
    try {
      // Fire-and-forget: per-payload latency tracking is not needed on these
      // low-rate topics; failures land in the catch below or the SDK log.
      void room.localParticipant
        .publishData(new TextEncoder().encode(json), { reliable, topic })
        .catch((err: unknown) => log.warn(`publishData('${topic}') failed`, err));
      return true;
    } catch (err) {
      log.warn(`publishData('${topic}') failed`, err);
      return false;
    }
  }

  setTrackSubscribed(identity: string, kind: RoomTrackKind, subscribed: boolean): boolean {
    const participant = this.room?.remoteParticipants.get(identity);
    if (!participant) return false;
    let any = false;
    for (const publication of participant.getTrackPublications()) {
      if (publication.kind === kind) {
        publication.setSubscribed(subscribed);
        any = true;
      }
    }
    return any;
  }

  setMicrophoneEnabled(enabled: boolean): void {
    this.micDesired = enabled;

    if (!enabled) {
      // Pause must kill the mic immediately: disabling the MediaStreamTrack
      // silences the source at once; the publication mute follows.
      if (this.micTrack) {
        this.micTrack.enabled = false;
        this.micMuted = true;
        void this.audioPublication?.mute().catch(() => undefined);
        this.reportDiagnostic('microphone muted');
      }
      return;
    }

    if (this.isRoomConnected && !this.micPublishInFlight) {
      void this.ensureMicrophone();
    }
  }

  useConfigEndpoint(urlOrHost: string): boolean {
    const url = normalizeConfigEndpoint(urlOrHost);
    if (!url) {
      log.warn(`Ignoring unusable config endpoint '${urlOrHost}'`);
      return false;
    }

    this.configEndpointOverride = url;
    this._activeEndpointDisplay = url;
    log.info(`Config endpoint set by hand: ${url}`);

    // The wearer pressed OK and expects an attempt now, not the next backoff
    // tick (same contract as the old manual address row).
    this.reconnectAttempt = 0;
    this.reconnectAtSeconds = -1;
    this.closedByUser = false;
    this.tearDownSession('manual endpoint entered');
    this.setState('connecting');
    this.beginConnect();
    return true;
  }

  clearConfigEndpointOverride(): void {
    this.configEndpointOverride = null;
  }

  // ---------------------------------------------------------------- connect

  private beginConnect(): void {
    if (this.connectInFlight || this.closedByUser) return;
    void this.connectFlow();
  }

  private async connectFlow(): Promise<void> {
    if (this.connectInFlight) return;
    this.connectInFlight = true;
    this.setState('connecting');
    this.reportDiagnostic('resolving room credentials');

    const credentials = await this.resolveCredentials();

    if (!credentialsUsable(credentials)) {
      this.connectInFlight = false;
      const reason = credentials.failureReason ?? 'no LiveKit URL/token configured';

      if (credentials.refusal) {
        this._availability.onRefused(credentials.refusal, this.now());
      }

      this.reportDiagnostic(`credential resolve failed - ${reason}`);
      this.setState('failed');

      // D24: a refused trainee FAILS AND STOPS. One attempt, one answer.
      // Scheduling a reconnect here would re-POST /api/token on every backoff
      // tick and claim the slot the instant it freed. The waiting window takes
      // over instead: a read-only status poll, and a Join button that enables
      // when the answer changes.
      if (this._availability.isWaiting) {
        const card = this._availability.describe(Date.now());
        this.emit('discoveryFailed', card.detail);
        this.reportDiagnostic(`waiting for the cell to free - ${card.detail}`);
        return;
      }

      this.emit(
        'discoveryFailed',
        credentials.refusal ? this._availability.describe(Date.now()).detail : reason,
      );
      this.scheduleReconnect('credentials unavailable');
      return;
    }

    this._activeEndpointDisplay = credentials.display ?? credentials.url;
    this.reportDiagnostic(`connecting room at ${this._activeEndpointDisplay}`);

    const newRoom = this.createRoom();
    this.wireRoomEvents(newRoom);
    this.room = newRoom;

    try {
      // Explicit options: autoSubscribe keeps assistants audible/visible by
      // default (D5 hides are per-assistant unsubscribes on top). Dynacast and
      // adaptiveStream(OFF) are Room construction options in livekit-client -
      // see the default factory.
      await newRoom.connect(credentials.url, credentials.token, { autoSubscribe: true });
    } catch (err) {
      this.connectInFlight = false;
      if (this.room !== newRoom) return; // torn down while connecting
      this.reportDiagnostic(`room connect failed (${err instanceof Error ? err.message : String(err)})`);
      this.tearDownSession('connect failed');
      this.setState('failed');
      this.scheduleReconnect('connect failed');
      return;
    }

    this.connectInFlight = false;
    if (this.room !== newRoom) return; // torn down while connecting

    this.reconnectAttempt = 0;
    this._availability.onConnected();
    this._roomName = newRoom.name ?? '';
    this._localIdentity = newRoom.localParticipant.identity ?? '';
    this.setState('connected');
    this.reportDiagnostic(`room '${this._roomName}' connected as '${this._localIdentity}'`);

    this.seedExistingParticipants(newRoom);

    if (this.micDesired && !this.micPublishInFlight) {
      void this.ensureMicrophone();
    }
  }

  private createRoom(): RoomLike {
    if (this.cfg.roomFactory) return this.cfg.roomFactory();
    // Deliberately dynamic-shaped: livekit-client is a peer of this library
    // and the structural RoomLike is the contract the service holds itself to.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    throw new Error(
      'LiveKitRoomService needs a roomFactory. Use createLiveKitRoomFactory() from the registrations module (it builds the real livekit-client Room), or supply a fake in tests.',
    );
  }

  private async fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.cfg.fetchTimeoutMs ?? 6000);
    try {
      return await this.fetchImpl(url, { ...init, signal: controller.signal, cache: 'no-store' });
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * RA-C10: `GET /api/config` (v2 payload) -> `POST /api/token` with the
   * wearer bearer -> LiveKit URL + JWT. The config's dev literals remain the
   * development/offline fallback (the spike's zero-infra path).
   */
  private async resolveCredentials(): Promise<RoomCredentials> {
    const credentials: RoomCredentials = {};
    const configUrl = this.effectiveConfigUrl;
    if (!configUrl) {
      this.useDevLiterals(credentials, 'no config endpoint configured');
      return credentials;
    }

    this._activeEndpointDisplay = configUrl;

    // The read-only status route a refused client polls (D24). Derived here,
    // while the config URL is in hand, so the waiting window has somewhere to
    // look even though the attempt that opens it fails.
    this.hostStatusEndpoint = resolveHostStatusEndpoint(configUrl, this.cfg.cell);

    // ---- GET /api/config (the discovery endpoint - extended payload).
    let requestUrl = configUrl;
    if (this.cfg.cell) {
      requestUrl += (requestUrl.includes('?') ? '&' : '?') + 'cell=' + encodeURIComponent(this.cfg.cell);
    }

    let config: AssistRemoteConfig | null = null;
    try {
      const response = await this.fetchWithTimeout(requestUrl);
      if (response.ok) {
        config = parseAssistRemoteConfig(await response.text());
      }
      if (!config) {
        this.useDevLiterals(
          credentials,
          `config fetch failed (${response.ok ? 'unparseable config payload' : `status ${response.status}`})`,
        );
        return credentials;
      }
    } catch (err) {
      this.useDevLiterals(
        credentials,
        `config fetch failed (${err instanceof Error ? err.message : String(err)})`,
      );
      return credentials;
    }

    this._lastRemoteConfig = config;
    this.emit('remoteConfig', config);
    this.reportDiagnostic(
      `config v${config.version} resolved: livekitUrl=${config.livekitUrl || '<none>'} ` +
        `room='${config.room}' wearerGate=${config.wearerToken ? 'published' : 'absent'}`,
    );

    // The client mirror of the server's transport flag: a config that
    // explicitly says aiortc while this build runs LiveKit is worth a loud
    // line - the operator flips both ends from the same KV entry.
    if (config.transport && config.transport.toLowerCase() !== 'livekit') {
      log.warn(
        `Served config says transport '${config.transport}' but this client is LiveKit-only.`,
      );
    }

    if (!config.livekitUrl) {
      this.useDevLiterals(
        credentials,
        `config v${config.version} carries no livekitUrl (deployment not migrated?)`,
      );
      return credentials;
    }

    // ---- POST /api/token - the wearer bearer model (D8): the
    // config-published secret gates the mint.
    const tokenEndpoint = resolveTokenEndpoint(config, configUrl);
    const secret = resolveWearerSecret(config, this.cfg.wearerToken);
    if (!tokenEndpoint || !secret) {
      this.useDevLiterals(
        credentials,
        !tokenEndpoint
          ? `no token endpoint derivable from the config URL '${configUrl}'`
          : "no wearer secret from ANY source - the served config has no 'wearerToken', " +
              "its 'server' carries no ?token=, and this client's configured wearerToken is empty. " +
              'Set QVS_AUTH_TOKEN on the vision host (it publishes the gate), or configure the ' +
              'wearer token to match QVS_WEARER_TOKEN on the Pages project',
      );
      return credentials;
    }

    this.reportDiagnostic(`requesting wearer token from ${tokenEndpoint}`);
    let status = 0;
    let bodyText: string | null = null;
    let transportError: string | null = null;
    try {
      const response = await this.fetchWithTimeout(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${secret}`,
        },
        body: wearerTokenRequestJson(this.cfg.deviceLabel ?? deviceLabel(), this.cfg.cell),
      });
      status = response.status;
      bodyText = await response.text();
    } catch (err) {
      transportError = err instanceof Error ? err.message : String(err);
    }

    const minted = status >= 200 && status < 300 && bodyText ? parseWearerTokenResponse(bodyText) : null;
    if (!minted) {
      // Plan-06 step 5c: 409 / 503 / 401 mean three different things to three
      // different people. Collapsing them into one "connection failed" is
      // what sends someone to find an admin for a problem they could have
      // solved themselves. The Function's own {error, message} is carried
      // through verbatim.
      const refusal = classifyTokenRefusal(status, bodyText, transportError);
      credentials.refusal = refusal;

      let reason = describeTokenRefusal(refusal, tokenEndpoint);
      if (status > 0 && bodyText && !refusal.serverMessage) {
        // Not the contract's {error, message} shape - quote what did arrive
        // rather than losing it.
        reason += `: ${truncate(bodyText, 300)}`;
      }

      if (refusal.kind === 'in_use') {
        // NOT useDevLiterals. A cell that is busy must not be joined by a
        // development URL/token: the room has one `wearer` identity, so
        // connecting anyway would evict the person already training - the
        // exact harm §9 exists to prevent, and the one the 409 just spared
        // them.
        credentials.failureReason = reason;
        log.warn(`${reason} - this cell is busy; waiting rather than retrying (D24).`);
        return credentials;
      }

      this.useDevLiterals(credentials, reason);
      return credentials;
    }

    // The mint's livekitUrl/room are authoritative (they came from the same
    // deployment record the token was scoped to).
    credentials.url = minted.livekitUrl || config.livekitUrl;
    credentials.token = minted.token;
    credentials.display = configUrl;
    return credentials;
  }

  /** Development/offline fallback: the configured literal URL + token, else a hard failure with the reason. */
  private useDevLiterals(credentials: RoomCredentials, reason: string): void {
    if (this.cfg.devServerUrl && this.cfg.devToken) {
      log.warn(`${reason} - using the development URL/token fallback.`);
      credentials.url = this.cfg.devServerUrl;
      credentials.token = this.cfg.devToken;
      credentials.display = this.cfg.devServerUrl;
      return;
    }

    // Whatever else is true of a failure, it gets a line: this branch is the
    // one every real deployment takes when discovery breaks, and it must
    // never fail silently.
    credentials.failureReason = reason;
    log.error(
      `Cannot obtain room credentials: ${reason}. ` +
        `(config endpoint: ${this.cfg.configUrl ?? ''}; cell: '${this.cfg.cell ?? ''}'). ` +
        'No development URL/token is configured, so there is nothing to fall back to.',
    );
  }

  /**
   * One poll of `GET /api/host/status` - Plan-06 §9.6 step 3, D24. Read-only,
   * unauthenticated, claims nothing; it must never become a token request, and
   * it must never connect anything by itself.
   */
  private async pollHostStatus(): Promise<void> {
    this.statusPollInFlight = true;
    this._availability.markPolled(this.now());

    if (!this.hostStatusEndpoint) {
      this._availability.onStatusUnsupported('no status endpoint for this config URL', this.now());
      this.statusPollInFlight = false;
      return;
    }

    try {
      const response = await this.fetchWithTimeout(this.hostStatusEndpoint);
      const body = await response.text();
      const now = this.now();

      if (response.status === 404 || response.status === 501) {
        // A client newer than the cloud it is talking to. Waiting on a route
        // that does not exist would strand the trainee.
        this._availability.onStatusUnsupported(`status endpoint answered ${response.status}`, now);
      } else if (!response.ok) {
        this._availability.onStatusUnavailable(`status ${response.status}`, now);
      } else {
        const document = parseHostStatusDocument(body);
        if (document) {
          this._availability.onStatus(document, now);
          this.reportDiagnostic(
            `host status: ${document.status}` + (document.detail ? ` - ${document.detail}` : ''),
          );
        } else {
          // "I could not read the answer" is never "it is free".
          this._availability.onStatusUnavailable('unreadable status document', now);
        }
      }
    } catch (err) {
      this._availability.onStatusUnavailable(
        err instanceof Error ? err.message : String(err),
        this.now(),
      );
    }

    this.statusPollInFlight = false;
  }

  private scheduleReconnect(reason: string, immediate = false): void {
    if (this.closedByUser || this.reconnectAtSeconds >= 0) return;

    const backoff = this.cfg.reconnectBackoffSeconds ?? DEFAULT_BACKOFF;
    let delay: number;
    if (immediate || backoff.length === 0) {
      delay = 0;
    } else {
      delay = backoff[Math.min(this.reconnectAttempt, backoff.length - 1)]!;
      this.reconnectAttempt += 1;
    }

    log.info(`Reconnecting in ${delay}s (${reason}, attempt ${this.reconnectAttempt})`);
    this.reconnectAtSeconds = this.now() + delay;
  }

  // ------------------------------------------------------------ room events

  private wireRoomEvents(target: RoomLike): void {
    const on = target.on.bind(target) as (event: string, listener: (...args: unknown[]) => void) => void;

    on('participantConnected', (participant) => {
      if (this.room !== target) return;
      const snapshot = snapshotParticipant(participant as RemoteParticipantLike);
      this.remoteById.set(snapshot.identity, snapshot);
      this.emit('participantJoined', snapshot);
    });

    on('participantDisconnected', (participant) => {
      if (this.room !== target) return;
      const identity = (participant as RemoteParticipantLike)?.identity ?? '';
      const snapshot = this.remoteById.get(identity);
      if (snapshot) {
        this.remoteById.delete(identity);
        this.emit('participantLeft', snapshot);
      }
    });

    on('trackPublished', (publication, participant) => {
      if (this.room !== target) return;
      this.emitTrackChange(publication as RemoteTrackPublicationLike, participant as RemoteParticipantLike, true);
    });

    on('trackUnpublished', (publication, participant) => {
      if (this.room !== target) return;
      this.emitTrackChange(publication as RemoteTrackPublicationLike, participant as RemoteParticipantLike, false);
    });

    on('trackMuted', (publication, participant) => {
      if (this.room !== target) return;
      this.emitRemoteMuteChange(publication as RemoteTrackPublicationLike, participant as RemoteParticipantLike, true);
    });

    on('trackUnmuted', (publication, participant) => {
      if (this.room !== target) return;
      this.emitRemoteMuteChange(publication as RemoteTrackPublicationLike, participant as RemoteParticipantLike, false);
    });

    on('trackSubscribed', (track, _publication, participant) => {
      if (this.room !== target) return;
      const kind = (track as { kind?: string }).kind;
      const media = (track as { mediaStreamTrack?: MediaStreamTrack }).mediaStreamTrack;
      if (kind === 'video' && media) {
        this.emit('remoteVideoTrack', {
          identity: (participant as RemoteParticipantLike)?.identity ?? '',
          track: media,
        });
      }
      // Remote AUDIO does need plumbing on the web (unlike Unity's platform
      // ADM): the assist session layer attaches subscribed audio tracks to an
      // AudioContext/element under the browser's autoplay rules (M3).
    });

    on('trackUnsubscribed', (track, _publication, participant) => {
      if (this.room !== target) return;
      if ((track as { kind?: string }).kind === 'video') {
        this.emit('remoteVideoTrack', {
          identity: (participant as RemoteParticipantLike)?.identity ?? '',
          track: null,
        });
      }
    });

    on('dataReceived', (payload, participant, _kind, topic) => {
      if (this.room !== target) return;
      let json: string;
      try {
        json = new TextDecoder().decode(payload as Uint8Array);
      } catch {
        return; // not UTF-8 - no hub topic carries binary
      }

      const message = {
        topic: (topic as string | undefined) ?? '',
        senderIdentity: (participant as RemoteParticipantLike | undefined)?.identity ?? '',
        json,
      };
      this.emit('data', message);

      // RA-C7: the detections feed switches transports here - same JSON, same
      // parser, new carrier.
      if (message.topic === RoomTopics.detections) {
        this.emit('detectionMessage', json);
        if (isReadyMessage(json)) this.emit('ready', undefined);
      }
    });

    on('connectionQualityChanged', (quality, participant) => {
      if (this.room !== target) return;
      const token = typeof quality === 'string' ? quality : 'lost';
      const identity = (participant as RemoteParticipantLike)?.identity ?? '';
      // Only remote legs are roster-relevant; our own quality still flows for
      // status surfaces, identified by the local identity.
      this.emit('participantQuality', {
        identity,
        quality: token === 'excellent' || token === 'good' || token === 'poor' ? token : 'lost',
      });
    });

    on('disconnected', () => {
      if (this.room !== target) return;
      this.reportDiagnostic('room disconnected');
      this.tearDownSession('room disconnected');
      if (this.closedByUser) {
        this.setState('closed');
        return;
      }
      this.setState('failed');
      this.scheduleReconnect('room disconnected');
    });

    on('reconnecting', () => {
      if (this.room !== target) return;
      // ICE hiccup semantics: can self-heal, not terminal.
      this.setState('disconnected');
      this.reportDiagnostic('room reconnecting');
    });

    on('reconnected', () => {
      if (this.room !== target) return;
      this.setState('connected');
      this.reportDiagnostic('room reconnected');
    });
  }

  private emitTrackChange(
    publication: RemoteTrackPublicationLike,
    participant: RemoteParticipantLike,
    published: boolean,
  ): void {
    const kind = trackKindOf(publication.kind);
    if (!kind) return;
    this.emit('remoteTrackChanged', {
      identity: participant?.identity ?? '',
      kind,
      published,
      muted: publication.isMuted === true,
    });
  }

  private emitRemoteMuteChange(
    publication: RemoteTrackPublicationLike,
    participant: RemoteParticipantLike,
    muted: boolean,
  ): void {
    // Local mute events (our own qualifier gate / consent mic) are not remote
    // roster changes.
    const identity = participant?.identity ?? '';
    if (!identity || identity === this._localIdentity) return;
    const kind = trackKindOf(publication.kind);
    if (!kind) return;
    this.emit('remoteTrackChanged', { identity, kind, published: true, muted });
  }

  private seedExistingParticipants(target: RoomLike): void {
    // Assistants (or the vision bot) may pre-date us in the room: replay them
    // as joins so the roster starts complete.
    for (const participant of target.remoteParticipants.values()) {
      const snapshot = snapshotParticipant(participant);
      this.remoteById.set(snapshot.identity, snapshot);
      this.emit('participantJoined', snapshot);

      for (const publication of participant.getTrackPublications()) {
        this.emitTrackChange(publication, participant, true);
      }
    }
  }

  // ---------------------------------------------------------- video publish

  private async publishVideo(): Promise<void> {
    this.videoPublishInFlight = true;

    const room = this.room;
    const track = this.liveVideoTrack();
    if (!room || !track) {
      this.videoPublishInFlight = false;
      return;
    }

    // ---- the resolution pin (RA-C1) --------------------------------------
    // The detector's accuracy is a function of the frame size it is fed: the
    // server infers AS SENT - no rescale, no letterbox. So the track is
    // constrained to the pinned stream size before publish. Unlike Unity
    // (which blits into an owned RenderTexture) the browser applies the
    // constraint inside the capture pipeline; a downstream capture-mode change
    // cannot move the track's dimensions while the constraint holds.
    const width = this.cfg.streamWidth ?? 640;
    const height = this.cfg.streamHeight ?? 480;
    const fps = this.cfg.targetFps ?? 30;
    try {
      await track.applyConstraints({ width: { exact: width }, height: { exact: height }, frameRate: fps });
    } catch (err) {
      // A camera that cannot make the exact size is worth a loud line, not a
      // silent wrong-size publish: detection quality silently degrades
      // otherwise. Publish anyway - a degraded feed beats no feed - but say so.
      log.warn(
        `Camera could not pin ${width}x${height}@${fps} (${err instanceof Error ? err.message : String(err)}); ` +
          `publishing at ${track.getSettings().width}x${track.getSettings().height} - detection quality may suffer.`,
      );
    }

    try {
      // RA-C1: simulcast OFF, resolution held under load - a frame at half
      // rate still detects, at quarter resolution it largely does not.
      //
      // degradationPreference 'maintain-resolution' holds resolution and
      // degrades framerate (the WebRTC RTCDegradationPreference names are the
      // truth). The default would permit downscaling under pressure.
      const publication = await room.localParticipant.publishTrack(track, {
        videoCodec: (this.cfg.useH264 ?? true) ? 'h264' : 'vp8',
        videoEncoding: {
          maxBitrate: this.cfg.maxBitrate ?? 1_500_000,
          maxFramerate: fps,
        },
        simulcast: false,
        degradationPreference: 'maintain-resolution',
        source: 'camera',
      });
      this.videoPublication = publication as unknown as LocalTrackPublicationLike;
    } catch (err) {
      this.videoPublishInFlight = false;
      this.reportDiagnostic(
        `video publish failed - retrying (${err instanceof Error ? err.message : String(err)})`,
      );
      this.nextVideoRetrySeconds = this.now() + 2;
      return;
    }

    this.videoPublishInFlight = false;
    this.videoPublished = true;

    // Size is stated explicitly because it is invisible from any video
    // surface - every viewer scales the track to its own element. The
    // detector is the only consumer that notices, and by then it just looks
    // like "detection got worse".
    const settings = track.getSettings();
    this.reportDiagnostic(
      `video published ${settings.width}x${settings.height}@${settings.frameRate ?? fps} ` +
        `codec=${(this.cfg.useH264 ?? true) ? 'h264' : 'vp8'} simulcast=off degradation=maintain-resolution ` +
        `pinned=${width}x${height}`,
    );
  }

  /**
   * The qualifier gate: MUTES the video track - the publication stays,
   * dynacast stops the flow, and unmuting resumes instantly (Plan-01 §2.1).
   * Never unpublishes.
   */
  private updateQualifierGate(): void {
    if (!(this.cfg.gateStreamingOnQuality ?? false) || !this.qualifier || !this.videoPublication) return;

    const shouldMute = !this.qualifier.shouldStream;
    if (shouldMute === this.lastQualifierGateMuted) return;

    this.lastQualifierGateMuted = shouldMute;
    if (shouldMute) {
      void this.videoPublication.mute().catch(() => undefined);
      this.reportDiagnostic('video muted - image quality below threshold');
    } else {
      void this.videoPublication.unmute().catch(() => undefined);
      this.reportDiagnostic('video unmuted - image quality recovered');
    }
  }

  // ------------------------------------------------------------- microphone

  private async ensureMicrophone(): Promise<void> {
    if (this.micTrack) {
      this.micTrack.enabled = true;
      this.micMuted = false;
      void this.audioPublication?.unmute().catch(() => undefined);
      this.reportDiagnostic('microphone unmuted');
      return;
    }

    this.micPublishInFlight = true;

    // Echo cancellation is not optional: without it the assistant's voice
    // goes straight back down the wire (the Unity port uses the platform ADM
    // for the same reason). Every failure is logged and swallowed - video,
    // detections and the training flow must never be held hostage by a
    // microphone.
    let track: MediaStreamTrack | undefined;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      track = stream.getAudioTracks()[0];
    } catch (err) {
      log.error(
        `Mic capture failed - the wearer can neither be heard nor unmute later without a retry: ${err instanceof Error ? err.message : String(err)}`,
      );
      this.reportDiagnostic('microphone unavailable - no outgoing audio');
      this.micPublishInFlight = false;
      return;
    }

    const room = this.room;
    if (!track || !room || !this.isRoomConnected) {
      track?.stop();
      this.micPublishInFlight = false;
      return;
    }

    try {
      const publication = await room.localParticipant.publishTrack(track, {
        source: 'microphone',
        audioPreset: { maxBitrate: 64_000 },
      });
      this.audioPublication = publication as unknown as LocalTrackPublicationLike;
      this.micTrack = track;
      this.micMuted = false;
      this.reportDiagnostic('microphone published (echo-cancelled)');
    } catch (err) {
      log.error(`Mic publish failed: ${err instanceof Error ? err.message : String(err)}`);
      track.stop();
      this.micPublishInFlight = false;
      return;
    }

    this.micPublishInFlight = false;

    // Consent may have flipped while the publish was in flight - the desire
    // is authoritative, never the request that started this.
    if (!this.micDesired && this.micTrack) {
      this.micTrack.enabled = false;
      this.micMuted = true;
      void this.audioPublication?.mute().catch(() => undefined);
    }
  }

  // ---------------------------------------------------------------- helpers

  private reportDiagnostic(diagnostic: string): void {
    this._lastDiagnostic = diagnostic;
    log.info(diagnostic);
    this.emit('diagnostic', diagnostic);
  }

  private setState(newState: WebRTCConnectionState): void {
    if (newState === this.state) return;
    this.state = newState;
    log.info(`State: ${newState}`);
    this.emit('stateChange', newState);
  }

  private tearDownSession(reason: string): void {
    this.videoPublication = undefined;
    this.videoPublished = false;
    this.videoPublishInFlight = false;
    this.nextVideoRetrySeconds = -1;
    this.lastQualifierGateMuted = false;

    // Best-effort: teardown must never throw.
    try {
      this.micTrack?.stop();
    } catch {
      /* ignore */
    }
    this.micTrack = undefined;
    this.audioPublication = undefined;
    this.micPublishInFlight = false;
    this.micMuted = false;

    const oldRoom = this.room;
    this.room = undefined;
    this.connectInFlight = false;

    if (oldRoom) {
      void oldRoom.disconnect().catch((err: unknown) => {
        log.warn(`Room disconnect during teardown (${reason})`, err);
      });
    }

    this.remoteById.clear();
    this._roomName = '';
    this._localIdentity = '';
  }
}

function snapshotParticipant(participant: RemoteParticipantLike): RoomParticipant {
  return {
    identity: participant.identity ?? '',
    name: participant.name ?? '',
    metadata: participant.metadata ?? '',
  };
}

function trackKindOf(kind: string): RoomTrackKind | null {
  return kind === 'video' || kind === 'audio' ? kind : null;
}

function isReadyMessage(json: string): boolean {
  try {
    const parsed = JSON.parse(json) as { type?: unknown };
    return typeof parsed === 'object' && parsed !== null && parsed.type === 'ready';
  } catch {
    return false;
  }
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}…`;
}

function deviceLabel(): string {
  if (typeof navigator === 'undefined') return 'WebXR Wearer';
  // The UA carries the headset model on Quest Browser; good enough for the
  // refusal card's "on <device>" sentence.
  return /OculusBrowser|Quest/i.test(navigator.userAgent) ? 'Quest (browser)' : 'WebXR Wearer';
}
